import { TextareaInput, TextInput } from '@/components/ui/form';
import type { AddReviewFormData, AddReviewFormErrors, AttorneyOption } from '../types';
import { AttorneyPickerInput } from '../ui/attorney-picker-input';
import { StarRating } from '../ui/star-rating';

export function ReviewFormFields({
  attorneys,
  formData,
  errors,
  onFieldChange,
  onAttorneySelect,
}: {
  attorneys: AttorneyOption[];
  formData: AddReviewFormData;
  errors: AddReviewFormErrors;
  onFieldChange: <K extends keyof AddReviewFormData>(key: K, value: AddReviewFormData[K]) => void;
  onAttorneySelect: (selection: { attorneyId: string; attorneyName: string }) => void;
}) {
  return (
    <>
      <AttorneyPickerInput
        id="review-attorney"
        attorneys={attorneys}
        valueId={formData.attorneyId}
        valueName={formData.attorneyName}
        onSelect={onAttorneySelect}
        errorText={errors.attorneyId}
      />
      <input type="hidden" name="review[attorney_id]" value={formData.attorneyId} />
      <input type="hidden" name="review[attorney_name]" value={formData.attorneyName} />

      <TextInput
        id="review-reviewer-name"
        name="review[reviewer_name]"
        label="Reviewer Name"
        helperText="Keep field blank for anonymous"
        value={formData.reviewerName}
        onChange={(event) => onFieldChange('reviewerName', event.target.value)}
        errorText={errors.reviewerName}
      />

      <TextInput
        id="review-reviewer-email"
        name="review[reviewer_email]"
        label="Reviewer Email"
        type="email"
        value={formData.reviewerEmail}
        onChange={(event) => onFieldChange('reviewerEmail', event.target.value)}
        errorText={errors.reviewerEmail}
      />

      <TextInput
        id="review-title"
        name="review[title]"
        label="Review Title *"
        value={formData.title}
        onChange={(event) => onFieldChange('title', event.target.value)}
        errorText={errors.title}
      />

      <TextareaInput
        id="review-description"
        name="review[description]"
        label="Review Text *"
        value={formData.description}
        onChange={(event) => onFieldChange('description', event.target.value)}
        errorText={errors.description}
        rows={4}
      />

      <TextareaInput
        id="review-summary"
        name="review[review_summary]"
        label="Review Summary"
        helperText="Add a short highlight from the review for use in review sections and widgets"
        value={formData.reviewSummary}
        onChange={(event) => onFieldChange('reviewSummary', event.target.value)}
        errorText={errors.reviewSummary}
        rows={3}
      />

      <div>
        <p className="text-sm font-semibold text-foreground">Star Rating</p>
        <StarRating
          rating={formData.rating}
          onChange={(rating) => onFieldChange('rating', rating)}
          className="mt-2"
          size="lg"
        />
        <input type="hidden" name="review[rating]" value={formData.rating ?? ''} />
        {errors.rating ? <p className="mt-1 text-sm text-destructive">{errors.rating}</p> : null}
      </div>
    </>
  );
}

export function requiredReviewFieldsFilled(formData: AddReviewFormData): boolean {
  return Boolean(formData.title.trim() && formData.description.trim());
}
